import { styled, SxProps, Theme, useTheme } from '@mui/material';
import { amber, green, red } from '@mui/material/colors';
import Tooltip from 'components/interface/tooltip/Tooltip';
import React from 'react';

const IconBase = styled('span', {
    shouldForwardProp: prop =>
        !['$size', '$color', '$style'].includes(prop as string),
})<{
    $size: number;
    $color: string;
    $style: string;
}>(({ $size, $color, $style }) => ({
    userSelect: 'none',
    fontSize: `${$size}px !important`,
    color: `${$color} !important`,
    fontVariationSettings: `${$style} !important`,
}));

function resolveSize(size: SymbolProps['size']): number {
    if (typeof size === 'number') return size;
    switch (size) {
        case 'xs':
            return 12;
        case 'sm':
            return 18;
        case 'md':
            return 20;
        case 'lg':
            return 24;
        case 'xl':
            return 32;
        case '2xl':
            return 40;
        default:
            return 20;
    }
}

function resolveColor(color: SymbolProps['color'], theme: Theme): string {
    switch (color) {
        case 'primary':
            return theme.palette.primary.main;
        case 'primary-dark':
            return theme.palette.primaryDark.main;
        case 'secondary':
            return theme.palette.secondary.main;
        case 'textPrimary':
            return theme.palette.text.primary;
        case 'textSecondary':
            return theme.palette.text.secondary;
        case 'grey':
            return theme.palette.grey[700];
        case 'amber':
            return amber[900];
        case 'green':
            return green[700];
        case 'red-dark':
            return red[900];
        case 'error':
            return theme.palette.error.main;
        case 'black':
            return '#000';
        case 'white':
            return '#fff';
        case 'inherit':
            return 'inherit';
        default:
            return color ?? 'inherit';
    }
}

export interface SymbolProps {
    symbol: string;
    size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
    color?:
        | 'primary'
        | 'primary-dark'
        | 'secondary'
        | 'textPrimary'
        | 'textSecondary'
        | 'grey'
        | 'amber'
        | 'green'
        | 'error'
        | 'black'
        | 'red-dark'
        | 'white'
        | 'inherit'
        | string;
    filled?: boolean;
    bold?: boolean;
    tooltip?: React.ReactNode;
    sx?: SxProps<Theme>;
}

export default function Symbol({
    symbol,
    size = 'sm',
    color = 'inherit',
    filled = false,
    bold = false,
    tooltip,
    sx,
}: SymbolProps) {
    const theme = useTheme();

    const resolvedSize = resolveSize(size);
    const resolvedColor = resolveColor(color, theme);

    const style = `'FILL' ${filled ? '1' : '0'}, 'wght' ${bold ? '700' : '400'}, 'GRAD' 0, 'opsz' ${filled ? '24' : '32'}`;

    const icon = (
        <IconBase
            $size={resolvedSize}
            $color={resolvedColor}
            $style={style}
            className='material-symbols-rounded'
            sx={sx}>
            {symbol}
        </IconBase>
    );

    return tooltip ? <Tooltip title={tooltip}>{icon}</Tooltip> : icon;
}
